/**
 * Framework-agnostic GA4 article outbound-click tracker.
 *
 * Sends `article_outbound_click` once per click on a link to an external
 * domain, but only for links inside the article body container (governed by
 * `containerSelector`/`excludeLinkSelector`) -- not nav/footer/social/
 * newsletter/comments/ads/trending-content, and not mailto:/tel:/javascript:/
 * same-page-fragment links (all excluded implicitly by the http(s)-protocol
 * and internal-hostname checks below). Gated on the page's existing GA4
 * Consent Mode signal, exactly like ArticleScrollDepthTracker.
 *
 * Event and field names (`article_outbound_click`, `article_view_id`,
 * `link_url`, `link_domain`, `outbound`, `page_location`) are fixed by
 * LIFT's ingestion contract and are never renamed by config -- this module
 * has no `eventPrefix` option, unlike ArticleScrollDepthTracker.
 *
 * Pair with ArticleScrollDepthTracker via a shared `viewSession` (see
 * createArticleViewSession) so both trackers' events carry the same
 * article_view_id for a given article view:
 *
 *   const viewSession = createArticleViewSession();
 *   const scrollTracker = createArticleScrollDepthTracker({ ...opts, viewSession });
 *   const clickTracker = createArticleOutboundClickTracker({
 *     containerSelector: ".article-body",
 *     internalDomains: ["dailyhive.com"],
 *     viewSession,
 *   });
 *   scrollTracker.start();
 *   clickTracker.start();
 *
 * Used standalone (no viewSession passed), it creates and owns its own
 * session, generating a fresh id on start()/switchArticle() the same way
 * ArticleScrollDepthTracker originates one for article_view.
 *
 * Known gap: a `click` listener does not fire on middle-click (browsers
 * dispatch `auxclick` for that button) -- Ctrl/Cmd+click still fires `click`
 * and is covered.
 */

import {
  createArticleViewSession,
  isArticleViewSession,
  type ArticleViewSession,
} from "../shared/createArticleViewSession";
import { createConsentGate } from "../shared/createConsentGate";
import { pushDataLayerEvent, type DataLayerTransport } from "../shared/pushDataLayerEvent";

const EVENT_NAME = "article_outbound_click";

export interface ArticleOutboundClickTrackerConfig {
  containerSelector?: string;
  excludeLinkSelector?: string;
  internalDomains?: string[];
  articleId?: string | number;
  canonicalUrl?: string;
  measurementId?: string;
  pageType?: string;
  transport?: DataLayerTransport;
  defaultConsentState?: "granted" | "denied";
  debug?: boolean;
  viewSession?: ArticleViewSession;
}

interface NormalizedConfig {
  containerSelector: string | null;
  excludeLinkSelector: string | null;
  internalDomains: string[];
  articleId?: string | number;
  canonicalUrl?: string;
  measurementId?: string;
  pageType: string | null;
  transport: DataLayerTransport;
  defaultConsentState: "granted" | "denied";
  debug: boolean;
}

export interface ArticleOutboundClickTracker {
  start(): void;
  switchArticle(next: {
    articleId?: string | number;
    containerSelector?: string;
    canonicalUrl?: string;
  }): void;
  destroy(): void;
  getDebugState(): Record<string, unknown> | null;
}

function warn(config: { debug: boolean }, ...args: unknown[]): void {
  if (config.debug && typeof console !== "undefined") {
    console.warn("[ArticleOutboundClickTracker]", ...args);
  }
}

function safely<T>(config: { debug: boolean }, fn: () => T): T | undefined {
  try {
    return fn();
  } catch (error) {
    warn(config, "swallowed an internal error", error);
    return undefined;
  }
}

// Resolves the container element from a CSS selector at the moment it's
// needed, rather than requiring the caller to have looked it up already.
// document.querySelector() itself already returns only the first match, so
// a selector matching multiple elements (e.g. a shared class name) can never
// resolve to more than one container.
function resolveContainer(selector?: string | null): Element | null {
  if (typeof document === "undefined" || !selector) return null;
  return document.querySelector(selector);
}

function stripQueryAndFragment(url?: string | null): string | undefined | null {
  if (!url) return url;
  try {
    const base = typeof location !== "undefined" ? location.href : undefined;
    const parsed = new URL(url, base);
    return `${parsed.origin}${parsed.pathname}`;
  } catch (error) {
    return String(url).split("?")[0].split("#")[0];
  }
}

function deriveCanonicalUrl(configuredUrl?: string | null): string {
  if (configuredUrl) return stripQueryAndFragment(configuredUrl) as string;
  if (typeof location === "undefined") return "";
  return stripQueryAndFragment(`${location.origin}${location.pathname}`) as string;
}

function normalizeConfig(
  userConfig?: ArticleOutboundClickTrackerConfig
): NormalizedConfig {
  const raw = userConfig || {};
  const transportExplicit = raw.transport === "gtag" || raw.transport === "gtm";
  const config: NormalizedConfig = {
    containerSelector:
      typeof raw.containerSelector === "string" ? raw.containerSelector : null,
    excludeLinkSelector:
      typeof raw.excludeLinkSelector === "string" ? raw.excludeLinkSelector : null,
    internalDomains: Array.isArray(raw.internalDomains)
      ? raw.internalDomains.filter((d) => typeof d === "string" && d)
      : [],
    articleId: raw.articleId,
    canonicalUrl: raw.canonicalUrl,
    measurementId: raw.measurementId,
    pageType: raw.pageType || null,
    transport: raw.transport === "gtm" ? "gtm" : "gtag",
    defaultConsentState:
      raw.defaultConsentState === "denied" ? "denied" : "granted",
    debug: !!raw.debug,
  };

  if (!transportExplicit) {
    warn(
      config,
      'transport was not set explicitly; defaulting to "gtag". Confirm this against the ' +
        "site's GTM container configuration before relying on it in production."
    );
  }
  if (!config.containerSelector) {
    warn(
      config,
      "no containerSelector provided; outbound clicks will never be measured."
    );
  }

  return config;
}

export function createArticleOutboundClickTracker(
  userConfig: ArticleOutboundClickTrackerConfig
): ArticleOutboundClickTracker {
  const config = normalizeConfig(userConfig);

  let container: Element | null = null;
  let listenerContainer: Element | null = null;
  let isListening = false;
  let articleId = config.articleId;
  let currentCanonicalUrl: string | undefined;

  let started = false;
  let destroyed = false;

  const ownsSession = !isArticleViewSession(userConfig && userConfig.viewSession);
  const viewSession: ArticleViewSession = ownsSession
    ? createArticleViewSession()
    : (userConfig.viewSession as ArticleViewSession);
  if (userConfig && userConfig.viewSession && ownsSession) {
    warn(
      config,
      "viewSession must implement { ensureViewId, getViewId, reset }; ignoring and creating an owned session."
    );
  }

  const gate = createConsentGate({
    defaultConsentState: config.defaultConsentState,
    onGrantedChange(after) {
      if (after) {
        beginTrackingIfNeeded();
      } else {
        detachClickListener();
      }
    },
  });

  function isInternalHostname(hostname: string): boolean {
    const pageHost =
      (typeof location !== "undefined" && location.hostname) || "";
    const normalizedPageHost = pageHost.toLowerCase();
    if (hostname === normalizedPageHost) return true;
    return config.internalDomains.some((raw) => {
      const domain = raw.toLowerCase();
      return hostname === domain || hostname.endsWith("." + domain);
    });
  }

  function sendEvent(extraFields: Record<string, unknown>) {
    if (destroyed) return;
    const dataLayer = gate.getDataLayer();
    if (!dataLayer) {
      warn(config, "no dataLayer available; dropping event", EVENT_NAME);
      return;
    }
    const viewId = viewSession.getViewId();
    if (!viewId) {
      warn(
        config,
        "dropping " + EVENT_NAME + ": no article_view_id established yet"
      );
      return;
    }
    const fields = {
      article_view_id: viewId,
      ...(articleId ? { article_id: articleId } : {}),
      ...(currentCanonicalUrl ? { article_canonical_url: currentCanonicalUrl } : {}),
      ...(config.pageType ? { page_type: config.pageType } : {}),
      outbound: 1,
      ...(typeof location !== "undefined" ? { page_location: location.href } : {}),
      ...extraFields,
    };
    pushDataLayerEvent({
      dataLayer,
      transport: config.transport,
      measurementId: config.measurementId,
      eventName: EVENT_NAME,
      fields,
    });
  }

  function handleClick(event: MouseEvent) {
    if (destroyed || !gate.isGranted()) return;
    const target = event.target as (Element & { closest?: typeof Element.prototype.closest }) | null;
    const anchor =
      target && typeof target.closest === "function"
        ? (target.closest("a[href]") as HTMLAnchorElement | null)
        : null;
    const currentTarget = event.currentTarget as Element | null;
    if (!anchor || !currentTarget || !currentTarget.contains(anchor)) return;
    if (config.excludeLinkSelector && anchor.closest(config.excludeLinkSelector)) {
      return;
    }
    if (anchor.protocol !== "http:" && anchor.protocol !== "https:") return;

    const hostname = anchor.hostname.toLowerCase();
    if (isInternalHostname(hostname)) return;

    const linkDomain = hostname.replace(/^www\./, "");
    sendEvent({ link_url: anchor.href, link_domain: linkDomain });
  }

  function attachClickListener() {
    if (destroyed || !container) return;
    if (isListening && listenerContainer === container) return;
    detachClickListener();
    container.addEventListener("click", handleClick as EventListener);
    listenerContainer = container;
    isListening = true;
  }

  function detachClickListener() {
    if (!isListening || !listenerContainer) return;
    listenerContainer.removeEventListener("click", handleClick as EventListener);
    listenerContainer = null;
    isListening = false;
  }

  function beginTrackingIfNeeded() {
    if (ownsSession) viewSession.ensureViewId();
    attachClickListener();
  }

  function initArticleState(next: {
    articleId?: string | number;
    containerSelector?: string;
    canonicalUrl?: string;
  }) {
    detachClickListener();
    if (next.containerSelector) {
      container = resolveContainer(next.containerSelector);
      if (!container) {
        warn(
          config,
          'no element matched containerSelector "' +
            next.containerSelector +
            '"; outbound clicks will never be measured.'
        );
      }
    }
    articleId = next.articleId;
    if ("canonicalUrl" in next) {
      currentCanonicalUrl = deriveCanonicalUrl(next.canonicalUrl);
    } else if (!currentCanonicalUrl) {
      currentCanonicalUrl = deriveCanonicalUrl(undefined);
    }
    if (ownsSession) viewSession.reset();
    if (gate.isGranted()) {
      beginTrackingIfNeeded();
    }
  }

  function start() {
    if (destroyed || started) return;
    started = true;
    safely(config, () => {
      gate.bootstrapOnce();
      initArticleState({
        articleId: config.articleId,
        containerSelector: config.containerSelector ?? undefined,
        canonicalUrl: config.canonicalUrl,
      });
    });
  }

  function switchArticle(next: {
    articleId?: string | number;
    containerSelector?: string;
    canonicalUrl?: string;
  }) {
    if (destroyed) return;
    safely(config, () => {
      if (!started) {
        started = true;
        gate.bootstrapOnce();
      }
      initArticleState(next || {});
    });
  }

  function destroy() {
    if (destroyed) return;
    destroyed = true;

    safely(config, () => detachClickListener());
    safely(config, () => gate.teardown());
  }

  function getDebugState() {
    return (
      safely(config, () => ({
        articleViewId: viewSession.getViewId(),
        consentState: gate.getConsentState(),
        effectiveAnalyticsStorage: gate.effectiveAnalyticsStorage(),
        observingDataLayer: gate.isObserving(),
        transport: config.transport,
        containerSelector: config.containerSelector,
        excludeLinkSelector: config.excludeLinkSelector,
        internalDomains: config.internalDomains,
        pageType: config.pageType,
        started,
        destroyed,
      })) || null
    );
  }

  return { start, switchArticle, destroy, getDebugState };
}
